import { defineStore } from 'pinia';
import { formatDistanceToNow } from 'date-fns';
import getPath from '@/utils/getPath';
import { apiGet, apiPost } from '@/utils/api';
import * as helpers from '@/utils/helpers';
import { toast } from 'vue3-toastify';

export const useAssignmentsStore = defineStore('assignments', {
  state: () => ({
    readyAssignments: [],
    wipAssignments: [],
    wipCursor: 0,
    wipRecords: 0,
    wipLimit: 25,
    wipPage: 1,
    wipDirection: 'asc',
    wipField: 'time_in_column',
    recentAssignments: [],
    recentCursor: 0,
    recentRecords: 0,
    recentLimit: 25,
    recentPage: 1,
    recentDirection: 'asc',
    recentField: 'last_activity',
    isLeadLoading: true,
    isWipLoading: true,
    isRecentLoading: true,
    emptyLead: false,
  }),
  getters: {
    sortWipAssignments: state => {
      return (order, field) => {
        return helpers.sort(order, field, state.wipAssignments);
      };
    },
    sortRecentAssignments: state => {
      return (order, field) => {
        return helpers.sort(order, field, state.recentAssignments);
      };
    },
    getWipAssignmentById: state => {
      return id => {
        return state.wipAssignments.find(
          assignment => id === assignment.work_item_id
        );
      };
    },
    getReadyAssignmentById: state => {
      return id => {
        return state.readyAssignments.find(
          assignment => id === assignment.work_item_id
        );
      };
    },
    workItemIsComplete: state => {
      return id => {
        return state.recentAssignments.find(
          assignment => parseInt(id) === parseInt(assignment.work_item_id)
        );
      };
    },
    workItemIsPriority: state => {
      return id => {
        return [
          ...state.readyAssignments,
          ...state.recentAssignments,
          ...state.wipAssignments,
        ].find(
          assignment =>
            parseInt(id) === parseInt(assignment.work_item_id) &&
            assignment.is_priority === true
        );
      };
    },
  },
  actions: {
    async fetchUserLead() {
      this.isLeadLoading = true;
      try {
        const responseData = await apiGet(getPath('assignments?type=lead'));

        this.readyAssignments = responseData?.data.map(assignment => ({
          ...assignment,
          time_in_column: assignment.last_auxo_activity_dtm
            ? formatDistanceToNow(new Date(assignment.last_auxo_activity_dtm))
            : null,
        }));
        this.emptyLead = false;
        this.isLeadLoading = false;
      } catch (err) {
        const errorBody = await err.response.json();
        console.error(errorBody.message);
        this.isLeadLoading = false;
      }
    },
    async fetchUserWip(
      auxo_user_id = null,
      page = 1,
      direction = 'asc',
      field = 'time_in_column'
    ) {
      this.isWipLoading = true;
      this.wipPage = page;
      this.wipDirection = direction;
      this.wipField = field;

      const limit = localStorage.getItem('wip_pagination')
        ? parseInt(localStorage.getItem('wip_pagination'), 10)
        : this.wipLimit;

      try {
        const payload = {
          cursor: page ? (page - 1) * limit : this.wipCursor,
          limit,
          field,
          direction,
        };

        let user_id = null;
        auxo_user_id ? (user_id = auxo_user_id) : (user_id = '');

        const responseData = await apiGet(
          getPath(`assignments?type=wip&user_id=${user_id}`)
        );

        this.wipAssignments = responseData?.data.map(assignment => ({
          ...assignment,
          time_in_column: assignment.mod_timest
            ? formatDistanceToNow(new Date(assignment.mod_timest), {
                includeSeconds: false,
              })
            : null,
          last_activity: assignment.mod_timest,
        }));

        if (field == 'time_in_column') {
          //edge case where field isn't the column to sort on
          this.wipAssignments.sort((a, b) => {
            if (a.mod_timest && b.mod_timest) {
              const dateA = new Date(a.mod_timest);
              const dateB = new Date(b.mod_timest);

              return direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
          });
        } else {
          this.wipAssignments = helpers.sort(
            direction,
            field,
            this.wipAssignments
          );
        }

        // TODO: Reimplement Pagination
        // this.wipCursor = response?.data?.cursor || 1;
        // this.wipRecords = response?.data?.records || 0;

        this.isWipLoading = false;
      } catch (err) {
        const errorBody = await err.response.json();
        console.error(errorBody.message);
        this.isWipLoading = false;
      }
    },
    async fetchRecentUserAssignments(
      auxo_user_id = null,
      page = 1,
      direction = 'desc',
      field = 'mod_timest'
    ) {
      this.isRecentLoading = true;
      this.recentPage = page;
      this.recentDirection = direction;
      this.recentField = field;
      try {
        const limit = localStorage.getItem('recent_lead_pagination')
          ? parseInt(localStorage.getItem('recent_lead_pagination'), 10)
          : this.recentLimit;

        const auxoUserId = auxo_user_id ? auxo_user_id : '';

        const responseData = await apiGet(
          getPath(`assignments/history?userId=${auxoUserId}`)
        );

        this.recentAssignments = responseData?.data.map(assignment => ({
          ...assignment,
          time_in_column: assignment.last_auxo_activity_dtm
            ? formatDistanceToNow(new Date(assignment.last_auxo_activity_dtm))
            : null,
        }));

        this.recentAssignments = helpers.sort(
          direction,
          field,
          this.recentAssignments
        );

        // TODO: Reimplement Pagination
        // this.recentCursor = responseData?.data?.cursor || 1;
        // this.recentRecords = responseData?.data?.records || 0;

        this.isRecentLoading = false;
      } catch (err) {
        const errorBody = await err.response.json();
        console.error(errorBody.message);
        this.isRecentLoading = false;
      }
    },
    async refreshTheLead() {
      this.isLeadLoading = true;
      try {
        const responseData = await apiPost(getPath('assignments'));

        if (!responseData.success) {
          const message =
            responseData.status_code == 409
              ? 'There was an error refreshing the lead! Please try again.'
              : responseData.message;
          toast.error(message);
        } else {
          this.readyAssignments = responseData?.data.map(assignment => ({
            ...assignment,
            time_in_column: assignment.last_auxo_activity_dtm
              ? formatDistanceToNow(new Date(assignment.last_auxo_activity_dtm))
              : null,
          }));
        }
        if (responseData.data.length == 0) {
          this.emptyLead = true;
        } else {
          this.emptyLead = false;
        }

        return responseData;
      } catch (err) {
        const errorBody = await err.response.json();
        console.error(errorBody.message);
        return null;
      } finally {
        this.isLeadLoading = false;
      }
    },
  },
});
